import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Pagination, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { v4 as uuidv4 } from "uuid";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./GallerySlider.scss";
import { IGif } from "../../services/types";
import { metrics, ViewRegion } from "../../utils/metrics";

interface IProps {
  gallery: IGif[];
  slidePrevArrow: MutableRefObject<any>;
  slideNextArrow: MutableRefObject<any>;
  sliderPagination: MutableRefObject<HTMLDivElement | null>;
  fullscreen: boolean;
  position: number;
  source: ViewRegion;
}

export const GallerySlider = (props: IProps) => {
  const [swiper, setSwiper] = useState<any>(null);
  const isSentMetrics = useRef<string[]>([]);

  const navigationSettings = {
    prevEl: props.slidePrevArrow.current,
    nextEl: props.slideNextArrow.current
  };

  useEffect(() => {
    if (swiper) {
      swiper.params.navigation.prevEl = props.slidePrevArrow.current;
      swiper.params.navigation.nextEl = props.slideNextArrow.current;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiper]);

  const paginationSettings = {
    el: props.sliderPagination.current,
    clickable: true,
    renderBullet: (index: number, className: string) => {
      return `<div class="dot ${className}"></div>`;
    },
  };

  const sendMetrics = useCallback((index: number) => {
    if (index === 0) {
      return;
    }

    if (!props.gallery[index]) {
      return;
    }

    const gif = props.gallery[index];

    if (isSentMetrics.current.indexOf(gif.id) === -1) {
      metrics.sendGifView({
        gifId: gif.id,
        tags: gif.tags,
        fullscreen: props.fullscreen,
        source: props.source,
        position: props.position
      });

      isSentMetrics.current.push(gif.id);
    }
  }, [props.fullscreen, props.gallery, props.source, props.position]);

  const handleSlideChange = useCallback((swiper: any) => {
    sendMetrics(swiper.activeIndex);
  }, [sendMetrics]);

  const renderSlide = (item: any) => (
    <SwiperSlide key={uuidv4()}>
      <img
        src={item.urls.hd ?? item.urls.sd}
        className="thumbnail"
        alt=""
        style={{
          aspectRatio: `${item.width} / ${item.height}`,
        }}
      />
    </SwiperSlide>
  );

  const renderSlides = props.gallery.map(renderSlide);

  return (
    <div className="GallerySlider">
      <Swiper
        slidesPerView={1}
        modules={[Navigation, Pagination]}
        navigation={navigationSettings}
        pagination={paginationSettings}
        onSwiper={setSwiper}
        onSlideChange={handleSlideChange}
      >
        {renderSlides}
      </Swiper>
    </div>
  );
};
